import { useAuthContext } from '@euler/app/flows/auth/context';
import { featureFlags } from '@euler/functions/featureFlags';
import {
  defaultTokenService,
  refreshAuthService,
} from '@euler/services/factory';
import { makeDebug } from '@euler/utils';
import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { useIsAppActive$ } from '@euler/utils/hooks/useIsAppActive';
import { useTimer } from '@euler/utils/hooks/useTimer';
import { jwtDecode } from 'jwt-decode';
import { useCallback, useEffect, useRef } from 'react';

const debug = makeDebug('auth:token-refresh:scheduler', __DEV__);

const REFRESH_AHEAD_MS = 5 * 60 * 1000;

async function getEffectiveExpiresAt(): Promise<number | null> {
  const [token, refreshTokenExpiresAt] = await Promise.all([
    defaultTokenService.getToken(),
    defaultTokenService.getRefreshTokenExpiry(),
  ]);

  let authTokenExpiresAt: number | null = null;
  if (token) {
    try {
      const { exp } = jwtDecode(token);
      if (exp != null) authTokenExpiresAt = exp * 1000;
    } catch {
      /* malformed token — ignore */
    }
  }

  const effective = Math.min(
    authTokenExpiresAt ?? Infinity,
    refreshTokenExpiresAt ?? Infinity,
  );
  return isFinite(effective) ? effective : null;
}

export const useTokenRefreshScheduler = () => {
  const timer = useTimer();
  const isActive$ = useIsAppActive$();
  const authContext = useAuthContext();
  const [authState] = useBehaviorSubject(authContext.authState$);
  const scheduleRef = useRef<() => Promise<void>>();

  const doSchedule = useCallback(async () => {
    if (!featureFlags.aheadOfTimeTokenRefreshEnabled) {
      debug('ahead-of-time token refresh is disabled, skipping');
      return;
    }

    const expiresAt = await getEffectiveExpiresAt();
    if (!expiresAt) {
      debug('no expiry info available, skipping schedule');
      return;
    }

    const delay = Math.max(0, expiresAt - Date.now() - REFRESH_AHEAD_MS);
    debug(
      'scheduling proactive refresh in %dms (effectiveExpiresAt: %s)',
      delay,
      new Date(expiresAt).toLocaleString(),
    );

    timer.schedule(async () => {
      const startedAt = Date.now();
      debug('proactive refresh timer fired');
      const rt = await defaultTokenService.getRefreshToken();
      if (!rt) {
        debug('no refresh token found, aborting');
        return;
      }
      try {
        debug('calling refresh endpoint from proactive scheduler');
        const result = await refreshAuthService.refresh(rt);
        await defaultTokenService.setToken(result.token);
        await defaultTokenService.setRefreshToken(result.refreshToken);
        await defaultTokenService.setRefreshTokenExpiry(
          Date.now() + result.refreshTokenExpiresIn * 1000,
        );
        debug(
          'proactive refresh succeeded in %dms, rescheduling (next refresh expiresIn: %ds)',
          Date.now() - startedAt,
          result.refreshTokenExpiresIn,
        );
        await scheduleRef.current?.();
      } catch (e) {
        debug(
          'proactive refresh failed after %dms, 401 middleware will handle expiry: %o',
          Date.now() - startedAt,
          e,
        );
      }
    }, delay);
  }, [timer]);

  scheduleRef.current = doSchedule;

  useEffect(() => {
    if (authState.isAuthenticated) {
      debug('authenticated, scheduling token refresh');
      void doSchedule();
    } else {
      debug('unauthenticated, clearing token refresh timer');
      timer.clear();
    }
  }, [authState.isAuthenticated, doSchedule, timer]);

  useEffect(() => {
    const sub = isActive$.subscribe(async active => {
      debug(
        'app active state changed: active=%s, authenticated=%s',
        active,
        authState.isAuthenticated,
      );
      if (!active || !authState.isAuthenticated) return;
      const expiresAt = await getEffectiveExpiresAt();
      if (!expiresAt) {
        debug('foregrounded with no token expiry info');
        return;
      }
      const remaining = expiresAt - Date.now();
      debug(
        'foreground token refresh check (remaining: %dms, expiresAt: %s)',
        remaining,
        new Date(expiresAt).toLocaleString(),
      );
      if (remaining <= REFRESH_AHEAD_MS) {
        debug(
          'foregrounded within refresh window (remaining: %dms), refreshing immediately',
          remaining,
        );
        timer.clear();
        void doSchedule();
      }
    });
    return () => sub.unsubscribe();
  }, [authState.isAuthenticated, doSchedule, isActive$, timer]);
};
